import { Bars } from "@Components/forecast/Bars";
import { HourProps } from "@Components/forecast/hourly/ItemWrapper";
import { BarsLabel } from "@Components/forecast/label/BarsLabel";
import { HourLabel } from "@Components/forecast/label/HourLabel";
import { WIND_DIRECTIONS } from "@Constants/forecast";
import { useTranslations } from "@Context/TranslationContext/TranslationContext";
import WindArrow from "@Resources/images/iconsets/wind-arrow.svg?react";
import WindCircle from "@Resources/images/iconsets/wind-circle.svg?react";
import { useDarkMode } from "usehooks-ts";

export const WIND_BARS_SCALING_FACTOR = 0.6;

export const WindItem = ({
  label,
  hour,
  boldLabel,
  withDivider,
}: HourProps & { boldLabel: boolean; withDivider: boolean }) => {
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslations();
  const { DD_DEG: windDirection, FF_KMH: avgWind, FX_KMH: gustSpeed } = hour;
  const WindIcon = windDirection > -1 ? WindArrow : WindCircle;

  const getWindDirectionText = (deg: number): string => {
    const windDirection = WIND_DIRECTIONS.find(({ range }) => {
      const [first, last] = range;
      return deg >= first && deg <= (last || first);
    });
    return windDirection
      ? t(`navigation.${windDirection.translationKey}`)
      : t("navigation.a11yDirectionNotFound", {
          windDirection: deg,
        });
  };

  return (
    <>
      <span
        className="m:sr-only m:h-[149px]! m:w-[64px]! m:lg:w-[80px]!"
        aria-label={t("navigation.a11yWindLabel", {
          label: label,
          windDirection: getWindDirectionText(windDirection),
          avgWind: avgWind,
          gustSpeed: gustSpeed,
        })}
        tabIndex={-1}
      />
      <div
        className="m:mt-2 m:flex m:flex-col m:items-center"
        aria-hidden="true"
        role="presentation">
        <HourLabel
          label={label}
          isBold={boldLabel}
          testId="hourly-navigation-wind-item-hour"
        />
        <div className="m:mt-1 m:mb-[7px]">
          <WindIcon
            style={{ rotate: `${windDirection}deg` }}
            stroke={isDarkMode ? "white" : "#092B49"}
            data-wind-direction={windDirection}
            data-testid="hourly-navigation-wind-item-direction"
          />
        </div>
      </div>
      <div className="m:flex m:flex-col" aria-hidden="true" role="presentation">
        <div className="m:relative">
          <Bars
            values={[gustSpeed]}
            unit="km/h"
            scalingFactor={WIND_BARS_SCALING_FACTOR}
            barBackgroundColorClassName="m:bg-special-wind-bar"
            withDivider={withDivider}
          />
        </div>
        <BarsLabel
          topLabel={{
            value: `${avgWind} / ${gustSpeed}`,
            className: "m:mt-3",
            testId: "hourly-navigation-wind-item-speeds",
          }}
          bottomLabel={{
            value: "km/h",
          }}
        />
      </div>
    </>
  );
};
